import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import SEO, { breadcrumb, faqSchema } from "@/components/SEO";
import { NET_PERSONAS } from "./netData";
import { ArrowRight, CheckCircle2, Sparkles, ShieldCheck, Newspaper } from "lucide-react";

const NAVY = "#0F2B5B";
const TEAL = "#14B8A6";

export default function NetPersona() {
  const { slug } = useParams();
  const p = NET_PERSONAS[slug];
  if (!p) return <Navigate to="/net" replace />;

  return (
    <div className="min-h-screen" style={{ background: "#F8FAFC", color: NAVY }} data-testid={`page-net-${p.slug}`}>
      <SEO
        title={p.seoTitle}
        description={p.seoDescription}
        canonical={`/net/${p.slug}`}
        image="/net-mascotte.png"
        schema={[
          faqSchema(p.faq),
          breadcrumb([
            { name: "Accueil", url: "/" },
            { name: "NET — Assistant IA", url: "/net" },
            { name: p.metier, url: `/net/${p.slug}` },
          ]),
        ]}
      />

      {/* HERO */}
      <section className="relative overflow-hidden pt-32 pb-14 lg:pt-40 lg:pb-16" style={{ background: "#ffffff" }}>
        <div className="max-w-6xl mx-auto px-5 lg:px-8 grid lg:grid-cols-[1fr_280px] gap-10 items-center">
          <div>
            <nav className="text-[12px]" style={{ color: "#94A3B8" }} aria-label="Fil d'ariane">
              <Link to="/net" className="hover:underline" style={{ color: TEAL }}>NET — Assistant IA</Link>
              <span className="mx-2">/</span>
              <span style={{ color: NAVY }}>{p.metier}</span>
            </nav>
            <div className="mt-5 inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-[12px]" style={{ background: "rgba(20,184,166,0.10)", border: "1px solid rgba(20,184,166,0.30)", color: TEAL }}>
              <Sparkles size={13} />
              <span style={{ fontWeight: 600 }}>NET pour {p.metier.toLowerCase()}</span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[1.06] tracking-[-0.03em]" style={{ color: NAVY, fontWeight: 600 }} data-testid="net-persona-title">
              {p.h1}
            </h1>
            <p className="mt-5 text-[16px] leading-relaxed max-w-2xl" style={{ color: "#64748B" }}>{p.intro}</p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link to="/demo" className="inline-flex items-center gap-2 transition hover:-translate-y-0.5" style={{ background: NAVY, color: "#ffffff", borderRadius: 999, padding: "12px 26px", fontSize: 14, fontWeight: 600 }} data-testid="net-persona-cta-demo">
                Planifier une démo <ArrowRight size={15} />
              </Link>
              <Link to="/net" className="inline-flex items-center gap-2 transition hover:-translate-y-0.5" style={{ background: "#ffffff", color: NAVY, border: "1px solid #CBD5E1", borderRadius: 999, padding: "11px 24px", fontSize: 14, fontWeight: 600 }}>
                Tout savoir sur NET
              </Link>
            </div>
          </div>
          <img
            src="/net-mascotte.png"
            alt={`NET, assistant IA pour ${p.metier.toLowerCase()}`}
            className="hidden lg:block w-full max-w-[250px] mx-auto drop-shadow-2xl"
            loading="eager"
          />
        </div>
      </section>

      {/* MISSIONS */}
      <section className="py-14 lg:py-18 px-5 lg:px-8" data-testid="net-persona-missions">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-display text-2xl lg:text-3xl" style={{ color: NAVY, fontWeight: 600 }}>
            Ce que NET fait pour vous
          </h2>
          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            {p.missions.map((m) => (
              <div key={m} className="flex items-start gap-3 p-5 rounded-xl" style={{ background: "#ffffff", border: "0.5px solid #E8EDF5" }}>
                <CheckCircle2 size={17} className="shrink-0 mt-0.5" style={{ color: TEAL }} />
                <span className="text-[14.5px] leading-relaxed" style={{ color: "#475569" }}>{m}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BÉNÉFICES */}
      <section className="py-14 lg:py-18 px-5 lg:px-8" style={{ background: "#ffffff" }} data-testid="net-persona-benefices">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-display text-2xl lg:text-3xl" style={{ color: NAVY, fontWeight: 600 }}>Les bénéfices concrets</h2>
          <div className="mt-8 grid sm:grid-cols-3 gap-5">
            {p.benefices.map(({ t, d }) => (
              <div key={t} className="p-6 rounded-xl" style={{ background: "#F8FAFC", border: "0.5px solid #E8EDF5" }}>
                <div className="font-semibold text-[16px]" style={{ color: NAVY }}>{t}</div>
                <p className="mt-2 text-[13.5px] leading-relaxed" style={{ color: "#64748B" }}>{d}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 flex items-center gap-3 p-5 rounded-xl" style={{ background: "rgba(20,184,166,0.07)", border: "0.5px solid rgba(20,184,166,0.25)" }}>
            <ShieldCheck size={20} className="shrink-0" style={{ color: TEAL }} />
            <p className="text-[13.5px] leading-relaxed" style={{ color: NAVY }}>
              <strong>IA souveraine</strong> — modèles européens (prioritairement Mistral), hébergement compatible
              HDS, RGPD, authentification professionnelle et supervision humaine. <strong>NET assiste, l'humain décide.</strong>
            </p>
          </div>
        </div>
      </section>

      {/* ARTICLES */}
      <section className="py-14 lg:py-18 px-5 lg:px-8" data-testid="net-persona-articles">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-bold" style={{ color: TEAL }}>
            <Newspaper size={14} /> Pour aller plus loin
          </div>
          <h2 className="font-display text-2xl lg:text-3xl mt-3" style={{ color: NAVY, fontWeight: 600 }}>Nos articles sur le sujet</h2>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {p.articles.map((a) => (
              <Link
                key={a.slug}
                to={`/blog/${a.slug}`}
                className="group p-6 rounded-xl transition hover:-translate-y-1"
                style={{ background: "#ffffff", border: "0.5px solid #E8EDF5" }}
                data-testid={`net-article-${a.slug}`}
              >
                <div className="font-semibold text-[15px] leading-snug" style={{ color: NAVY }}>{a.title}</div>
                <span className="mt-3 inline-flex items-center gap-1.5 text-[13px] font-semibold transition group-hover:gap-2.5" style={{ color: TEAL }}>
                  Lire l'article <ArrowRight size={14} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 lg:py-18 px-5 lg:px-8" style={{ background: "#ffffff" }} data-testid="net-persona-faq">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-display text-2xl lg:text-3xl text-center" style={{ color: NAVY, fontWeight: 600 }}>Questions fréquentes</h2>
          <div className="mt-8 space-y-4">
            {p.faq.map(({ q, a }) => (
              <details key={q} className="group p-5 rounded-xl" style={{ background: "#F8FAFC", border: "0.5px solid #E8EDF5" }}>
                <summary className="cursor-pointer font-semibold text-[15px] list-none flex items-center justify-between gap-4" style={{ color: NAVY }}>
                  {q}
                  <ArrowRight size={15} className="shrink-0 transition group-open:rotate-90" style={{ color: TEAL }} />
                </summary>
                <p className="mt-3 text-[14px] leading-relaxed" style={{ color: "#64748B" }}>{a}</p>
              </details>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link to="/demo" className="inline-flex items-center gap-2 transition hover:-translate-y-0.5" style={{ background: NAVY, color: "#ffffff", borderRadius: 999, padding: "13px 30px", fontSize: 14.5, fontWeight: 600 }} data-testid="net-persona-final-cta">
              Découvrir NET en démo <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
